import React, { useEffect, useState } from 'react';
import { Box, Chip, Tooltip, Skeleton } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import SchoolIcon from '@mui/icons-material/School';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import StarsIcon from '@mui/icons-material/Stars';
import GavelIcon from '@mui/icons-material/Gavel';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import {
  badgeService,
  CaregiverBadge,
  BadgeType,
} from '../services/badgeService';

// FEAT-039 — CaregiverBadgeRow.
// Renders the trust badge row produced by GET /api/caregivers/:id/badges.
// Public-vs-authenticated filtering happens server-side; this component
// renders whatever the API returns, so unauthenticated callers see only
// the public subset (VERIFIED, SPECIALTY, STATE_CERTIFIED,
// STATE_AGENCY_REGISTERED) and authenticated callers also see
// CAREVA_COMPLETION_CREDENTIAL and SPECIALTY_CREDENTIALED.
//
// The badge row is purely presentational — it never surfaces admin-only
// score data or internal trust signals.

interface CaregiverBadgeRowProps {
  caregiverId: string;
  size?: 'small' | 'medium';
  density?: 'compact' | 'spacious';
}

const BADGE_ICON: Record<BadgeType, React.ReactElement> = {
  VERIFIED: <VerifiedIcon fontSize="small" />,
  SPECIALTY: <StarsIcon fontSize="small" />,
  CAREVA_COMPLETION_CREDENTIAL: <SchoolIcon fontSize="small" />,
  STATE_CERTIFIED: <GavelIcon fontSize="small" />,
  SPECIALTY_CREDENTIALED: <WorkspacePremiumIcon fontSize="small" />,
  STATE_AGENCY_REGISTERED: <HowToRegIcon fontSize="small" />,
};

const BADGE_TOOLTIP: Record<BadgeType, string> = {
  VERIFIED:
    "A Careva administrator has reviewed this caregiver's documents and confirmed their identity.",
  SPECIALTY:
    'This caregiver has identified a specialty area on their profile.',
  CAREVA_COMPLETION_CREDENTIAL:
    'This caregiver has completed the required Careva learning modules and acknowledged each one.',
  STATE_CERTIFIED:
    'This caregiver has uploaded a state-issued certification that an admin has reviewed and accepted.',
  SPECIALTY_CREDENTIALED:
    'This caregiver has uploaded specialty training credentials that an admin has reviewed and accepted.',
  STATE_AGENCY_REGISTERED:
    'This caregiver has provided a state licensing-agency registration number.',
};

const BADGE_COLOR: Record<BadgeType, 'success' | 'primary' | 'info' | 'secondary'> = {
  VERIFIED: 'success',
  SPECIALTY: 'primary',
  CAREVA_COMPLETION_CREDENTIAL: 'info',
  STATE_CERTIFIED: 'success',
  SPECIALTY_CREDENTIALED: 'secondary',
  STATE_AGENCY_REGISTERED: 'primary',
};

function defaultLabel(type: BadgeType): string {
  switch (type) {
    case 'VERIFIED':
      return 'Verified Caregiver';
    case 'SPECIALTY':
      return 'Specialty';
    case 'CAREVA_COMPLETION_CREDENTIAL':
      return 'Careva Learning Credential';
    case 'STATE_CERTIFIED':
      return 'State Certified';
    case 'SPECIALTY_CREDENTIALED':
      return 'Specialty Credentialed';
    case 'STATE_AGENCY_REGISTERED':
      return 'Registered';
  }
}

const CaregiverBadgeRow: React.FC<CaregiverBadgeRowProps> = ({
  caregiverId,
  size = 'small',
  density = 'compact',
}) => {
  const [badges, setBadges] = useState<CaregiverBadge[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    badgeService
      .listForCaregiver(caregiverId)
      .then((response) => {
        if (mounted) {
          setBadges(response.badges);
        }
      })
      .catch(() => {
        if (mounted) setBadges([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [caregiverId]);

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}
        data-testid="caregiver-badge-row-loading"
      >
        <Skeleton variant="rounded" width={120} height={28} />
        <Skeleton variant="rounded" width={120} height={28} />
      </Box>
    );
  }

  if (!badges || badges.length === 0) {
    return null;
  }

  const gap = density === 'compact' ? 0.5 : 1;

  return (
    <Box
      sx={{ display: 'flex', gap, flexWrap: 'wrap', alignItems: 'center' }}
      data-testid="caregiver-badge-row"
      role="list"
      aria-label="Caregiver trust badges"
    >
      {badges.map((badge) => {
        const label = badge.metadata?.label ?? defaultLabel(badge.type);
        return (
          <Tooltip key={badge.id} title={BADGE_TOOLTIP[badge.type]} arrow>
            <Chip
              icon={BADGE_ICON[badge.type]}
              label={label}
              size={size}
              color={BADGE_COLOR[badge.type]}
              variant="outlined"
              data-testid={`caregiver-badge-${badge.type.toLowerCase()}`}
              aria-label={`${label} — ${BADGE_TOOLTIP[badge.type]}`}
              role="listitem"
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default CaregiverBadgeRow;
